import { createFileRoute } from '@tanstack/react-router';
import { useMemo, useState } from 'react';

import { fmt, fmtDate, type FxRevaluationListRow, type FxRevaluationStatus } from '@dcc-erp/shared';
import { Icon } from '@dcc-erp/ui';

import { PageHead } from '../components/layout';
import { useActiveCompanyId } from '../lib/queries/companies';
import {
  useFxRates,
  useFxRevaluations,
  useReverseFxRevaluation,
  useRunFxRevaluation,
  useUpsertFxRate,
} from '../lib/queries/fx-revaluation';

// =============================================================================
// FX Revaluation — spec §38 (TAS 21)
//
// Keeps the prototype DOM intact (same KPI cards, 3-step concept banner, 4 tabs)
// and wires Run / Reverse / Manage Rates to the real API. The foreign-balance
// preview list has no API source yet (the backend computes balances internally
// during a Run), so it renders a real empty-state until ArInvoice / BankAccount
// grow per-row currency codes + a balances endpoint is exposed.
// =============================================================================

type TabKey = 'balance' | 'reval' | 'reversal' | 'history';
type FxType = 'AR' | 'AP' | 'ADVANCE';
type CurrencyCode = 'USD' | 'EUR' | 'JPY' | 'CNY';
type RateDate = '2026-04-30' | '2026-05-31';

interface FxCurrency {
  code: 'THB' | CurrencyCode;
  name: string;
  symbol: string;
  base?: true;
}

interface FxBalanceRaw {
  id: string;
  entity: 'DCC' | 'TTOP';
  type: FxType;
  acct: string;
  name: string;
  currency: CurrencyCode;
  amountFX: number;
  txnDate: string;
  txnRate: number;
  txnTHB: number;
}

interface FxBalance extends FxBalanceRaw {
  closingRate: number;
  thbAtClosing: number;
  fxDiff: number;
  gainLoss: number;
}

interface CurrencyAgg {
  gain: number;
  loss: number;
  count: number;
}

interface FxSummary {
  byCurrency: Record<string, CurrencyAgg>;
  totalGain: number;
  totalLoss: number;
  netGL: number;
}

interface FxHistoryRow {
  period: string;
  date: string;
  gain: number;
  loss: number;
  net: number;
  reversed: boolean;
  reversalDate: string;
}

const FX_CURRENCIES: readonly FxCurrency[] = [
  { code: 'THB', name: 'บาท ไทย', symbol: '฿', base: true },
  { code: 'USD', name: 'US Dollar', symbol: '$' },
  { code: 'EUR', name: 'Euro', symbol: '€' },
  { code: 'JPY', name: 'Japanese Yen', symbol: '¥' },
  { code: 'CNY', name: 'Chinese Yuan', symbol: '¥' },
];

const FX_RATES: Record<RateDate, Record<CurrencyCode, number>> = {
  '2026-04-30': { USD: 36.28, EUR: 39.42, JPY: 0.2325, CNY: 5.024 },
  '2026-05-31': { USD: 36.44, EUR: 39.88, JPY: 0.234, CNY: 5.042 },
};

// Current period — locked to May 2026 to match the prototype copy. A real
// implementation would derive this from the active accounting period selector.
const CURRENT_PERIOD = '202605';
const CURRENT_RATE_DATE: RateDate = '2026-05-31';

// No mock balances. The foreign-balance preview has no API source yet (the
// backend computes balances internally during a Run), so the list is empty
// until per-row currency tagging + a balances endpoint are wired — a real
// empty-state, not fabricated rows.
const FX_BALANCES_RAW: readonly FxBalanceRaw[] = [];

const FX_BALANCES: readonly FxBalance[] = FX_BALANCES_RAW.map((b) => {
  const closingRate = FX_RATES[CURRENT_RATE_DATE][b.currency];
  const thbAtClosing = Math.round(b.amountFX * closingRate);
  const fxDiff = thbAtClosing - b.txnTHB;
  const gainLoss = b.type === 'AP' ? -fxDiff : fxDiff;
  return { ...b, closingRate, thbAtClosing, fxDiff, gainLoss };
});

const TYPE_BADGE: Record<FxType, { l: string; c: string }> = {
  AR: { l: 'AR', c: 'info' },
  AP: { l: 'AP', c: 'negative' },
  ADVANCE: { l: 'มัดจำรับ', c: 'warning' },
};

const RUN_STEPS: readonly string[] = [
  'ดึงยอดคงค้าง AR / AP / มัดจำรับ ที่เป็นสกุลต่างประเทศ ทุก Entity',
  'ดึงอัตราแลกเปลี่ยน ธปท. ณ วันที่ 31 พ.ค. 2026',
  '',
  'สร้าง JV Revaluation วันที่ 31 พ.ค. 2026',
  'Schedule Reversal JV วันที่ 1 มิ.ย. 2026 (Auto-post)',
  'ส่ง Email Notify ฝ่ายบัญชี + CFO',
];

// 'YYYYMM' → 'MM/YYYY' display label.
function periodDisplay(period: string): string {
  return `${period.slice(4, 6)}/${period.slice(0, 4)}`;
}
function periodToFirstOfNextMonth(period: string): string {
  const year = Number.parseInt(period.slice(0, 4), 10);
  const month = Number.parseInt(period.slice(4, 6), 10);
  const ny = month === 12 ? year + 1 : year;
  const nm = month === 12 ? 1 : month + 1;
  return `${ny}-${String(nm).padStart(2, '0')}-01`;
}

export const Route = createFileRoute('/fxreval')({
  component: FxRevalPage,
});

function FxRevalPage() {
  const companyId = useActiveCompanyId();
  const [tab, setTab] = useState<TabKey>('balance');
  const [showRun, setShowRun] = useState(false);
  const [showRates, setShowRates] = useState(false);

  const { data: serverRevals } = useFxRevaluations(companyId, { period: CURRENT_PERIOD });
  const { data: historyRevals } = useFxRevaluations(companyId);
  const runMut = useRunFxRevaluation(companyId);
  const reverseMut = useReverseFxRevaluation(companyId);

  const summary = useMemo<FxSummary>(() => {
    const byCurrency: Record<string, CurrencyAgg> = {};
    FX_BALANCES.forEach((b) => {
      const cur = byCurrency[b.currency] ?? { gain: 0, loss: 0, count: 0 };
      cur.count++;
      if (b.gainLoss > 0) cur.gain += b.gainLoss;
      else cur.loss += -b.gainLoss;
      byCurrency[b.currency] = cur;
    });
    const totalGain = FX_BALANCES.filter((b) => b.gainLoss > 0).reduce((s, b) => s + b.gainLoss, 0);
    const totalLoss = FX_BALANCES.filter((b) => b.gainLoss < 0).reduce(
      (s, b) => s + -b.gainLoss,
      0,
    );
    return { byCurrency, totalGain, totalLoss, netGL: totalGain - totalLoss };
  }, []);

  const tabs: readonly (readonly [TabKey, string])[] = [
    ['balance', `ยอดคงค้างสกุลต่างประเทศ (${FX_BALANCES.length})`],
    ['reval', 'Revaluation Calculation'],
    ['reversal', 'Reversal ต้นงวดถัดไป'],
    ['history', 'ประวัติ Revaluation'],
  ];

  const gainCount = FX_BALANCES.filter((b) => b.gainLoss > 0).length;
  const lossCount = FX_BALANCES.filter((b) => b.gainLoss < 0).length;

  const postedThisPeriod = serverRevals?.filter((r) => r.status === 'POSTED').length ?? 0;
  const reversedThisPeriod = serverRevals?.filter((r) => r.status === 'REVERSED').length ?? 0;

  return (
    <div className="page">
      <PageHead
        crumbs={['รายงานและภาษี', 'Multi-currency & FX Revaluation']}
        title="ปรับอัตราแลกเปลี่ยน (FX Revaluation) — งวด 31 พ.ค. 2026"
        actions={
          <>
            <button className="btn" onClick={() => setShowRates(true)}>
              <Icon name="reports" size={14} /> Manage FX Rates
            </button>
            <button
              className="btn"
              onClick={() => {
                if (!companyId) return;
                if (
                  !window.confirm(`Reverse FX revaluations for ${periodDisplay(CURRENT_PERIOD)}?`)
                )
                  return;
                reverseMut.mutate({ period: CURRENT_PERIOD });
              }}
              disabled={reverseMut.isPending || postedThisPeriod === 0}
            >
              <Icon name="refresh" size={14} /> Reverse
            </button>
            <button
              className="btn primary"
              onClick={() => setShowRun(true)}
              disabled={runMut.isPending}
            >
              <Icon name="refresh" size={14} /> Run Revaluation
            </button>
          </>
        }
      />

      <div className="kpi-grid">
        <div className="kpi">
          <div className="label">
            <span className="dot" />
            ยอดสกุลต่างประเทศคงค้าง
          </div>
          <div className="value tnum">
            {FX_BALANCES.length} <span className="unit">รายการ</span>
          </div>
          <div className="trend faint">{Object.keys(summary.byCurrency).length} สกุลเงิน</div>
        </div>
        <div className="kpi">
          <div className="label">
            <span className="dot" style={{ background: 'var(--positive)' }} />
            Unrealized Gain
          </div>
          <div className="value tnum" style={{ color: 'var(--positive)' }}>
            +{fmt(summary.totalGain)} <span className="unit">บาท</span>
          </div>
          <div className="trend faint">{gainCount} รายการ</div>
        </div>
        <div className="kpi">
          <div className="label">
            <span className="dot" style={{ background: 'var(--negative)' }} />
            Unrealized Loss
          </div>
          <div className="value tnum" style={{ color: 'var(--negative)' }}>
            −{fmt(summary.totalLoss)} <span className="unit">บาท</span>
          </div>
          <div className="trend faint">{lossCount} รายการ</div>
        </div>
        <div className="kpi">
          <div className="label">
            <span className="dot" style={{ background: 'var(--brand)' }} />
            Net Unrealized G/L
          </div>
          <div
            className="value tnum"
            style={{ color: summary.netGL >= 0 ? 'var(--positive)' : 'var(--negative)' }}
          >
            {summary.netGL >= 0 ? '+' : ''}
            {fmt(summary.netGL)} <span className="unit">บาท</span>
          </div>
          <div className="trend faint">
            POSTED {postedThisPeriod} · REVERSED {reversedThisPeriod}
          </div>
        </div>
      </div>

      <div
        style={{
          background: '#fafafa',
          border: '1px solid var(--border)',
          borderRadius: 8,
          padding: '14px 18px',
          marginBottom: 'var(--section-gap)',
        }}
      >
        <div className="section-title" style={{ margin: '0 0 10px' }}>
          หลักการ — FX Revaluation ตาม TAS 21
        </div>
        <div
          style={{ display: 'grid', gridTemplateColumns: 'repeat(3,1fr)', gap: 12, fontSize: 14 }}
        >
          <div
            style={{
              padding: '10px 14px',
              background: 'white',
              borderRadius: 6,
              border: '1px solid var(--border)',
            }}
          >
            <b style={{ color: '#b54708' }}>① Transaction Date</b>
            <div className="meta" style={{ marginTop: 4 }}>
              บันทึกใช้อัตรา ณ วันเกิดรายการ (Historical Rate)
            </div>
          </div>
          <div
            style={{
              padding: '10px 14px',
              background: 'white',
              borderRadius: 6,
              border: '1px solid var(--border)',
            }}
          >
            <b style={{ color: '#1a56a6' }}>② ปลายงวด (Closing)</b>
            <div className="meta" style={{ marginTop: 4 }}>
              ปรับเป็นอัตรา ธปท. · บันทึก Unrealized G/L ใน P&L
            </div>
          </div>
          <div
            style={{
              padding: '10px 14px',
              background: 'white',
              borderRadius: 6,
              border: '1px solid var(--border)',
            }}
          >
            <b style={{ color: '#117a3d' }}>③ ต้นงวดถัดไป (Reversal)</b>
            <div className="meta" style={{ marginTop: 4 }}>
              กลับรายการ Revaluation · เพื่อให้ Settle ถูกอัตราใหม่
            </div>
          </div>
        </div>
      </div>

      <div className="page-tabs">
        {tabs.map(([k, l]) => (
          <div key={k} className={`pt ${tab === k ? 'active' : ''}`} onClick={() => setTab(k)}>
            {l}
          </div>
        ))}
      </div>

      {tab === 'balance' && <FXBalanceTab />}
      {tab === 'reval' && <FXRevalTab summary={summary} />}
      {tab === 'reversal' && <FXReversalTab summary={summary} />}
      {tab === 'history' && <FXHistoryTab serverRows={historyRevals} />}

      {showRun && (
        <RunRevalModal
          summary={summary}
          isPending={runMut.isPending}
          error={runMut.error instanceof Error ? runMut.error.message : null}
          onConfirm={() => {
            if (!companyId) return;
            runMut.mutate({ period: CURRENT_PERIOD }, { onSuccess: () => setShowRun(false) });
          }}
          onClose={() => setShowRun(false)}
        />
      )}
      {showRates && <FXRatesModal companyId={companyId} onClose={() => setShowRates(false)} />}
    </div>
  );
}

function FXBalanceTab() {
  return (
    <div className="tbl-shell">
      <div className="filter-row">
        <select className="select" defaultValue="all">
          <option value="all">Entity: ทั้งหมด</option>
          <option value="DCC">DCC</option>
          <option value="TTOP">TTOP</option>
          <option value="RCI">RCI</option>
        </select>
        <select className="select" defaultValue="all">
          <option value="all">ประเภท: ทั้งหมด</option>
          <option value="AR">AR ลูกหนี้</option>
          <option value="AP">AP เจ้าหนี้</option>
          <option value="ADV">Advance Receipt มัดจำรับ</option>
        </select>
        <select className="select" defaultValue="all">
          <option value="all">สกุลเงิน: ทั้งหมด</option>
          <option value="USD">USD</option>
          <option value="EUR">EUR</option>
          <option value="JPY">JPY</option>
          <option value="CNY">CNY</option>
        </select>
      </div>
      <table className="tbl">
        <thead>
          <tr>
            <th>เลขที่</th>
            <th>Entity</th>
            <th>ประเภท</th>
            <th>คู่ค้า / รายการ</th>
            <th className="num">สกุล</th>
            <th className="num">จำนวน FX</th>
            <th className="num">อัตรา ณ บันทึก</th>
            <th className="num">THB ณ บันทึก</th>
            <th className="num">อัตรา ณ สิ้นงวด</th>
            <th className="num">THB ณ สิ้นงวด</th>
            <th className="num">Gain/Loss</th>
          </tr>
        </thead>
        <tbody>
          {FX_BALANCES.length === 0 && (
            <tr>
              <td colSpan={10} className="muted" style={{ padding: 28, textAlign: 'center' }}>
                ยังไม่มียอดคงค้างสกุลต่างประเทศ
              </td>
            </tr>
          )}
          {FX_BALANCES.map((b) => {
            const t = TYPE_BADGE[b.type];
            return (
              <tr key={b.id}>
                <td className="code">
                  <b>{b.id}</b>
                </td>
                <td>
                  <b>{b.entity}</b>
                </td>
                <td>
                  <span className={`badge ${t.c}`}>{t.l}</span>
                </td>
                <td>
                  {b.name}
                  <div className="meta">
                    {b.acct} · {fmtDate(b.txnDate)}
                  </div>
                </td>
                <td className="num">
                  <b>{b.currency}</b>
                </td>
                <td className="num tnum">{fmt(b.amountFX, 2)}</td>
                <td className="num tnum muted">{b.txnRate.toFixed(4)}</td>
                <td className="num tnum">{fmt(b.txnTHB)}</td>
                <td className="num tnum" style={{ color: 'var(--brand)', fontWeight: 600 }}>
                  {b.closingRate.toFixed(4)}
                </td>
                <td className="num tnum">
                  <b>{fmt(b.thbAtClosing)}</b>
                </td>
                <td
                  className="num tnum"
                  style={{
                    color:
                      b.gainLoss > 0
                        ? 'var(--positive)'
                        : b.gainLoss < 0
                          ? 'var(--negative)'
                          : 'inherit',
                    fontWeight: 700,
                  }}
                >
                  {b.gainLoss > 0 ? '+' : ''}
                  {fmt(b.gainLoss)}
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
}

interface SummaryProp {
  summary: FxSummary;
}

function FXRevalTab({ summary }: SummaryProp) {
  const arNet = FX_BALANCES.filter((b) => b.type === 'AR').reduce((s, b) => s + b.gainLoss, 0);
  const apNet = FX_BALANCES.filter((b) => b.type === 'AP').reduce((s, b) => s + -b.gainLoss, 0);
  const advNet = FX_BALANCES.filter((b) => b.type === 'ADVANCE').reduce(
    (s, b) => s + b.gainLoss,
    0,
  );
  return (
    <>
      <div className="grid-2-eq" style={{ marginBottom: 'var(--section-gap)' }}>
        <div className="card">
          <div className="card-head">
            <h3>สรุป Unrealized G/L แยกตามสกุลเงิน</h3>
          </div>
          <div className="card-body flush">
            <table className="tbl">
              <thead>
                <tr>
                  <th>สกุล</th>
                  <th className="num">รายการ</th>
                  <th className="num">Gain</th>
                  <th className="num">Loss</th>
                  <th className="num">Net G/L</th>
                </tr>
              </thead>
              <tbody>
                {Object.entries(summary.byCurrency).map(([cur, d]) => {
                  const net = d.gain - d.loss;
                  return (
                    <tr key={cur}>
                      <td>
                        <b>{cur}</b>
                      </td>
                      <td className="num">{d.count}</td>
                      <td className="num tnum pos">+{fmt(d.gain)}</td>
                      <td className="num tnum neg">−{fmt(d.loss)}</td>
                      <td
                        className="num tnum"
                        style={{
                          color: net >= 0 ? 'var(--positive)' : 'var(--negative)',
                          fontWeight: 600,
                        }}
                      >
                        {net >= 0 ? '+' : ''}
                        {fmt(net)}
                      </td>
                    </tr>
                  );
                })}
                <tr className="total">
                  <td>รวม</td>
                  <td className="num">{FX_BALANCES.length}</td>
                  <td className="num tnum pos">+{fmt(summary.totalGain)}</td>
                  <td className="num tnum neg">−{fmt(summary.totalLoss)}</td>
                  <td
                    className="num tnum"
                    style={{ color: summary.netGL >= 0 ? 'var(--positive)' : 'var(--negative)' }}
                  >
                    {summary.netGL >= 0 ? '+' : ''}
                    {fmt(summary.netGL)}
                  </td>
                </tr>
              </tbody>
            </table>
          </div>
        </div>
        <div className="card">
          <div className="card-head">
            <h3>JV ที่จะสร้าง — Revaluation</h3>
          </div>
          <div className="card-body flush">
            <table className="tbl">
              <thead>
                <tr>
                  <th>บัญชี</th>
                  <th>ชื่อ</th>
                  <th className="num">Dr</th>
                  <th className="num">Cr</th>
                </tr>
              </thead>
              <tbody>
                <tr>
                  <td className="code">1122-FX</td>
                  <td>ปรับปรุงลูกหนี้สกุลต่างประเทศ (Net)</td>
                  <td className="num tnum">{fmt(arNet)}</td>
                  <td className="num">—</td>
                </tr>
                <tr>
                  <td className="code">2110-FX</td>
                  <td>ปรับปรุงเจ้าหนี้สกุลต่างประเทศ (Net)</td>
                  <td className="num tnum">{fmt(apNet)}</td>
                  <td className="num">—</td>
                </tr>
                <tr>
                  <td className="code">2160-FX</td>
                  <td>ปรับปรุงมัดจำรับสกุลต่างประเทศ</td>
                  <td className="num tnum">{fmt(advNet)}</td>
                  <td className="num">—</td>
                </tr>
                <tr>
                  <td className="code">4310</td>
                  <td>กำไรอัตราแลกเปลี่ยน (Unrealized)</td>
                  <td className="num">—</td>
                  <td className="num tnum">{fmt(summary.totalGain)}</td>
                </tr>
                <tr>
                  <td className="code">5310</td>
                  <td>ขาดทุนอัตราแลกเปลี่ยน (Unrealized)</td>
                  <td className="num">—</td>
                  <td className="num tnum">{fmt(summary.totalLoss)}</td>
                </tr>
              </tbody>
            </table>
          </div>
        </div>
      </div>

      <div className="card">
        <div className="card-head">
          <h3>รายละเอียดการคำนวณ — แต่ละรายการ</h3>
        </div>
        <div className="card-body flush">
          <table className="tbl">
            <thead>
              <tr>
                <th>เลขที่</th>
                <th>คู่ค้า</th>
                <th>ประเภท</th>
                <th className="num">จำนวน FX</th>
                <th className="num">อัตราเดิม</th>
                <th className="num">อัตราใหม่</th>
                <th className="num">Δ Rate</th>
                <th className="num">FX Diff THB</th>
                <th className="num">G/L</th>
              </tr>
            </thead>
            <tbody>
              {FX_BALANCES.length === 0 && (
                <tr>
                  <td colSpan={8} className="muted" style={{ padding: 28, textAlign: 'center' }}>
                    ไม่มีรายการให้คำนวณ
                  </td>
                </tr>
              )}
              {FX_BALANCES.map((b) => {
                const rateDiff = b.closingRate - b.txnRate;
                return (
                  <tr key={b.id}>
                    <td className="code">{b.id}</td>
                    <td>{b.name}</td>
                    <td>
                      <span className="badge brand">{b.type}</span>
                    </td>
                    <td className="num tnum">
                      {fmt(b.amountFX, 2)} {b.currency}
                    </td>
                    <td className="num tnum">{b.txnRate.toFixed(4)}</td>
                    <td className="num tnum">{b.closingRate.toFixed(4)}</td>
                    <td
                      className="num tnum"
                      style={{ color: rateDiff > 0 ? 'var(--positive)' : 'var(--negative)' }}
                    >
                      {rateDiff > 0 ? '+' : ''}
                      {rateDiff.toFixed(4)}
                    </td>
                    <td className="num tnum">{fmt(b.fxDiff)}</td>
                    <td
                      className="num tnum"
                      style={{
                        color: b.gainLoss > 0 ? 'var(--positive)' : 'var(--negative)',
                        fontWeight: 700,
                      }}
                    >
                      {b.gainLoss > 0 ? '+' : ''}
                      {fmt(b.gainLoss)}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </div>
    </>
  );
}

function FXReversalTab({ summary }: SummaryProp) {
  const arNet = FX_BALANCES.filter((b) => b.type === 'AR').reduce((s, b) => s + b.gainLoss, 0);
  const apNet = FX_BALANCES.filter((b) => b.type === 'AP').reduce((s, b) => s + -b.gainLoss, 0);
  const advNet = FX_BALANCES.filter((b) => b.type === 'ADVANCE').reduce(
    (s, b) => s + b.gainLoss,
    0,
  );
  return (
    <div className="card">
      <div className="card-head">
        <h3>Reversal Entry — กลับรายการต้นงวดถัดไป (1 มิ.ย. 2026)</h3>
      </div>
      <div className="card-body">
        <div
          style={{
            padding: '14px 16px',
            background: '#e8eff8',
            borderRadius: 8,
            marginBottom: 14,
            fontSize: 14,
            lineHeight: 1.55,
          }}
        >
          <b>💡 หลักการ Reversal:</b> รายการ Revaluation ณ สิ้นงวด เป็นเพียง <b>Estimate</b> ·
          เมื่อขึ้นต้นงวดถัดไป ต้องกลับรายการให้ AR/AP/Advance Receipt กลับเป็นยอดเดิม (อัตราเดิม) ·
          เพื่อให้ตอน Settle จริง คำนวณ <b>Realized G/L</b> ได้ถูกต้อง (ใช้อัตราจ่ายจริง −
          อัตราเดิม)
        </div>
        <table
          className="tbl"
          style={{ border: '1px solid var(--border)', borderRadius: 6, overflow: 'hidden' }}
        >
          <thead>
            <tr>
              <th>บัญชี</th>
              <th>ชื่อ</th>
              <th className="num">Dr</th>
              <th className="num">Cr</th>
            </tr>
          </thead>
          <tbody>
            <tr>
              <td className="code">4310</td>
              <td>กำไรอัตราแลกเปลี่ยน (Reverse)</td>
              <td className="num tnum">{fmt(summary.totalGain)}</td>
              <td className="num">—</td>
            </tr>
            <tr>
              <td className="code">5310</td>
              <td>ขาดทุนอัตราแลกเปลี่ยน (Reverse)</td>
              <td className="num tnum">{fmt(summary.totalLoss)}</td>
              <td className="num">—</td>
            </tr>
            <tr>
              <td className="code">1122-FX</td>
              <td>กลับการปรับปรุงลูกหนี้สกุลต่างประเทศ</td>
              <td className="num">—</td>
              <td className="num tnum">{fmt(arNet)}</td>
            </tr>
            <tr>
              <td className="code">2110-FX</td>
              <td>กลับการปรับปรุงเจ้าหนี้สกุลต่างประเทศ</td>
              <td className="num">—</td>
              <td className="num tnum">{fmt(apNet)}</td>
            </tr>
            <tr>
              <td className="code">2160-FX</td>
              <td>กลับการปรับปรุงมัดจำรับสกุลต่างประเทศ</td>
              <td className="num">—</td>
              <td className="num tnum">{fmt(advNet)}</td>
            </tr>
            <tr className="total">
              <td colSpan={2}>รวม Reversal</td>
              <td className="num tnum">{fmt(summary.totalGain + summary.totalLoss)}</td>
              <td className="num tnum">{fmt(summary.totalGain + summary.totalLoss)}</td>
            </tr>
          </tbody>
        </table>
        <div
          style={{
            marginTop: 14,
            padding: '12px 14px',
            background: '#fef3e8',
            borderRadius: 6,
            fontSize: 14,
            lineHeight: 1.55,
          }}
        >
          <b>📅 Schedule:</b> ระบบจะ <b>Auto-post</b> Reversal JV ในวันที่ 1 มิ.ย. 2026 เวลา 00:01 ·
          ก่อน Post จะส่ง Email Notify ฝ่ายบัญชี · ผู้ใช้สามารถยกเลิก Schedule ได้ภายใน 24 ชั่วโมง
        </div>
      </div>
    </div>
  );
}

interface FXHistoryTabProps {
  serverRows: FxRevaluationListRow[] | undefined;
}

function FXHistoryTab({ serverRows }: FXHistoryTabProps) {
  // Aggregate server rows by period (one row per period in the history view —
  // sum gain/loss across currencies). Falls back to the static prototype rows
  // when no server data exists yet.
  const rows: FxHistoryRow[] = useMemo(() => {
    if (!serverRows || serverRows.length === 0) return [];
    const byPeriod = new Map<
      string,
      {
        gain: number;
        loss: number;
        net: number;
        reversed: boolean;
        statuses: FxRevaluationStatus[];
      }
    >();
    for (const r of serverRows) {
      const cur = byPeriod.get(r.period) ?? {
        gain: 0,
        loss: 0,
        net: 0,
        reversed: true,
        statuses: [],
      };
      const gl = Number(r.unrealizedGainLoss);
      if (gl > 0) cur.gain += gl;
      else cur.loss += -gl;
      cur.net += gl;
      cur.statuses.push(r.status);
      if (r.status !== 'REVERSED') cur.reversed = false;
      byPeriod.set(r.period, cur);
    }
    return Array.from(byPeriod.entries())
      .sort(([a], [b]) => b.localeCompare(a))
      .map(([period, agg]) => ({
        period: periodDisplay(period),
        date: `${period.slice(0, 4)}-${period.slice(4, 6)}-28`,
        gain: Math.round(agg.gain),
        loss: Math.round(agg.loss),
        net: Math.round(agg.net),
        reversed: agg.reversed,
        reversalDate: periodToFirstOfNextMonth(period),
      }));
  }, [serverRows]);

  return (
    <div className="tbl-shell">
      <table className="tbl">
        <thead>
          <tr>
            <th>งวด</th>
            <th>วันที่ Revaluation</th>
            <th className="num">Gain</th>
            <th className="num">Loss</th>
            <th className="num">Net G/L</th>
            <th>Reversal</th>
            <th>Status</th>
          </tr>
        </thead>
        <tbody>
          {rows.length === 0 && (
            <tr>
              <td colSpan={7} className="muted" style={{ padding: 28, textAlign: 'center' }}>
                ยังไม่มีประวัติการปรับอัตราแลกเปลี่ยน
              </td>
            </tr>
          )}
          {rows.map((h) => (
            <tr key={h.period}>
              <td>
                <b>{h.period}</b>
              </td>
              <td>{fmtDate(h.date)}</td>
              <td className="num tnum pos">+{fmt(h.gain)}</td>
              <td className="num tnum neg">−{fmt(h.loss)}</td>
              <td
                className="num tnum"
                style={{
                  color: h.net >= 0 ? 'var(--positive)' : 'var(--negative)',
                  fontWeight: 700,
                }}
              >
                {h.net >= 0 ? '+' : ''}
                {fmt(h.net)}
              </td>
              <td>
                {h.reversed ? (
                  <span>
                    {fmtDate(h.reversalDate)} <span className="badge positive">✓</span>
                  </span>
                ) : (
                  <span className="badge warning">รอ</span>
                )}
              </td>
              <td>
                <span className="badge positive">
                  <Icon name="check" size={10} /> Completed
                </span>
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

interface RunRevalModalProps {
  summary: FxSummary;
  isPending: boolean;
  error: string | null;
  onConfirm: () => void;
  onClose: () => void;
}

function RunRevalModal({ summary, isPending, error, onConfirm, onClose }: RunRevalModalProps) {
  return (
    <div
      style={{
        position: 'fixed',
        inset: 0,
        background: 'rgba(15,15,20,0.45)',
        display: 'grid',
        placeItems: 'center',
        zIndex: 100,
        padding: 24,
      }}
      onClick={onClose}
    >
      <div
        onClick={(e) => e.stopPropagation()}
        style={{
          background: 'white',
          borderRadius: 12,
          width: 720,
          maxWidth: '100%',
          boxShadow: '0 28px 80px rgba(0,0,0,0.18)',
          overflow: 'hidden',
        }}
      >
        <div
          style={{
            padding: '18px 22px',
            borderBottom: '1px solid var(--border)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
          }}
        >
          <div>
            <h3
              style={{
                margin: 0,
                fontSize: 18,
                fontWeight: 600,
                fontFamily: 'var(--font-display)',
              }}
            >
              Run FX Revaluation — งวด {periodDisplay(CURRENT_PERIOD)}
            </h3>
            <div className="meta" style={{ marginTop: 2 }}>
              ระบบจะสร้าง Revaluation JV + Schedule Reversal วันถัดไป
            </div>
          </div>
          <button className="btn ghost" onClick={onClose} style={{ padding: '0 8px' }}>
            <Icon name="x" size={18} />
          </button>
        </div>
        <div style={{ padding: '22px' }}>
          <div className="section-title" style={{ margin: '0 0 8px' }}>
            ขั้นตอนการทำงาน
          </div>
          {RUN_STEPS.map((t, i) => {
            const label =
              i === 2
                ? `คำนวณ FX Diff: ${FX_BALANCES.length} รายการ → Net G/L ${fmt(summary.netGL)}`
                : t;
            return (
              <div
                key={i}
                style={{
                  display: 'flex',
                  gap: 10,
                  padding: '10px 12px',
                  background: '#fafafa',
                  borderRadius: 6,
                  marginBottom: 6,
                  fontSize: 14,
                }}
              >
                <div
                  style={{
                    width: 24,
                    height: 24,
                    borderRadius: '50%',
                    background: 'var(--brand)',
                    color: 'white',
                    display: 'grid',
                    placeItems: 'center',
                    fontWeight: 700,
                    flexShrink: 0,
                  }}
                >
                  {i + 1}
                </div>
                {label}
              </div>
            );
          })}
          {error && (
            <div
              style={{
                marginTop: 10,
                padding: '10px 12px',
                background: '#fef0ef',
                color: 'var(--negative)',
                borderRadius: 6,
                fontSize: 13,
              }}
            >
              {error}
            </div>
          )}
        </div>
        <div
          style={{
            padding: '14px 22px',
            borderTop: '1px solid var(--border)',
            display: 'flex',
            justifyContent: 'flex-end',
            gap: 8,
            background: '#fafafa',
          }}
        >
          <button className="btn ghost" onClick={onClose} disabled={isPending}>
            ยกเลิก
          </button>
          <button className="btn primary" onClick={onConfirm} disabled={isPending}>
            <Icon name="check" size={14} /> {isPending ? 'กำลังประมวลผล…' : 'Run Revaluation'}
          </button>
        </div>
      </div>
    </div>
  );
}

interface FXRatesModalProps {
  companyId: string | undefined;
  onClose: () => void;
}

function FXRatesModal({ companyId, onClose }: FXRatesModalProps) {
  const { data: rates } = useFxRates(companyId, {
    dateFrom: '2026-04-01',
    dateTo: '2026-05-31',
  });
  const upsertMut = useUpsertFxRate(companyId);

  // Build a 2-column comparison (30 Apr vs 31 May) from server rates if any,
  // otherwise fall back to the spec's static numbers.
  const ratesByDate = useMemo(() => {
    const map: Record<string, Record<string, number>> = {
      '2026-04-30': { ...FX_RATES['2026-04-30'] },
      '2026-05-31': { ...FX_RATES['2026-05-31'] },
    };
    if (rates) {
      for (const r of rates) {
        const key = new Date(r.rateDate).toISOString().slice(0, 10);
        map[key] ??= {};
        map[key][r.currency] = Number(r.midRate);
      }
    }
    return map;
  }, [rates]);

  const confirmRates = () => {
    if (!companyId) return;
    // Persist the May 31 rates so the run can find them.
    const mayRates = ratesByDate['2026-05-31'];
    if (!mayRates) return;
    Object.entries(mayRates).forEach(([currency, midRate]) => {
      upsertMut.mutate({
        currency,
        rateDate: '2026-05-31',
        midRate,
        source: 'BOT',
      });
    });
  };

  return (
    <div
      style={{
        position: 'fixed',
        inset: 0,
        background: 'rgba(15,15,20,0.45)',
        display: 'grid',
        placeItems: 'center',
        zIndex: 100,
        padding: 24,
      }}
      onClick={onClose}
    >
      <div
        onClick={(e) => e.stopPropagation()}
        style={{
          background: 'white',
          borderRadius: 12,
          width: 780,
          maxWidth: '100%',
          boxShadow: '0 28px 80px rgba(0,0,0,0.18)',
          overflow: 'hidden',
        }}
      >
        <div
          style={{
            padding: '18px 22px',
            borderBottom: '1px solid var(--border)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
          }}
        >
          <h3
            style={{ margin: 0, fontSize: 18, fontWeight: 600, fontFamily: 'var(--font-display)' }}
          >
            จัดการอัตราแลกเปลี่ยน (ธปท.)
          </h3>
          <button className="btn ghost" onClick={onClose} style={{ padding: '0 8px' }}>
            <Icon name="x" size={18} />
          </button>
        </div>
        <div style={{ padding: '22px' }}>
          <table className="tbl" style={{ border: '1px solid var(--border)', borderRadius: 6 }}>
            <thead>
              <tr>
                <th>สกุล</th>
                <th>ชื่อ</th>
                <th className="num">อัตรา 30 เม.ย.</th>
                <th className="num">อัตรา 31 พ.ค.</th>
                <th className="num">% เปลี่ยน</th>
              </tr>
            </thead>
            <tbody>
              {FX_CURRENCIES.filter((c): c is FxCurrency & { code: CurrencyCode } => !c.base).map(
                (c) => {
                  const old = ratesByDate['2026-04-30']?.[c.code] ?? 0;
                  const cur = ratesByDate['2026-05-31']?.[c.code] ?? 0;
                  const chg = old ? ((cur - old) / old) * 100 : 0;
                  return (
                    <tr key={c.code}>
                      <td>
                        <b>{c.code}</b> <span style={{ fontSize: 16 }}>{c.symbol}</span>
                      </td>
                      <td>{c.name}</td>
                      <td className="num tnum">{old.toFixed(4)}</td>
                      <td className="num tnum">
                        <b style={{ color: 'var(--brand)' }}>{cur.toFixed(4)}</b>
                      </td>
                      <td
                        className="num tnum"
                        style={{
                          color: chg > 0 ? 'var(--positive)' : 'var(--negative)',
                          fontWeight: 600,
                        }}
                      >
                        {chg > 0 ? '+' : ''}
                        {chg.toFixed(2)}%
                      </td>
                    </tr>
                  );
                },
              )}
            </tbody>
          </table>
        </div>
        <div
          style={{
            padding: '14px 22px',
            borderTop: '1px solid var(--border)',
            display: 'flex',
            justifyContent: 'flex-end',
            gap: 8,
            background: '#fafafa',
          }}
        >
          <button className="btn ghost" onClick={onClose}>
            ปิด
          </button>
          <button
            className="btn"
            onClick={() => {
              /* TODO(phase-3): sync from BOT */
            }}
          >
            <Icon name="refresh" size={14} /> Sync จาก ธปท.
          </button>
          <button
            className="btn primary"
            onClick={confirmRates}
            disabled={upsertMut.isPending || !companyId}
          >
            <Icon name="check" size={14} />{' '}
            {upsertMut.isPending ? 'กำลังบันทึก…' : 'ยืนยันใช้อัตรานี้'}
          </button>
        </div>
      </div>
    </div>
  );
}
